import React from 'react';
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { SystemNameplateInfo } from "../../graphql/queries/systemNameplateInfo";
import { SystemIotConnection } from "../../graphql/queries/systemIotConnection";
import _ from "lodash";
import { Chip, Typography } from "@mui/joy";
import { Box, Button, Skeleton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { KeyValueInformation, getStatusChipColors, getStatusChipLabel } from "../AssetNameplate";
import NodeImageActionButton, { NODE_IMAGE_HOVER_TARGET_CLASS } from "../NodeImageActionButton";
import { SYSTEM_TYPES, getSystemTypeLabel } from "../../config/systemTypes";
import { getFallbackAssetImage } from "../../hooks/useIndustryType";
import { getNodeImageThumbnail } from "../../utils/nodeDisplayImage";
import { buildNodeImageTarget } from "../../hooks/useNodeImage";
import { OpenInNewOutlined } from "@mui-symbols-material/w300";

interface Props {
    systemId: string;
    onOpenSystem?: () => void;
    /** Disables click-to-navigate on the title/image (e.g. when rendered inside a modal). */
    disableNavigation?: boolean;
    /** Hides the node-image upload/remove button on the image (non-detail-view mounts). */
    hideImageActions?: boolean;
}

const SkeletonKeyValue = ({ title }: { title: string }) => (
    <Box display="flex" width="100%">
        <Typography level="body-sm" sx={{ width: "40%", maxWidth: "160px" }} color="neutral">
            {title}:
        </Typography>
        <Skeleton variant="text" width="60%" height={20} />
    </Box>
);

const SystemNameplate = ({ systemId, onOpenSystem, disableNavigation, hideImageActions }: Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Title/image navigate to the system detail view, unless disabled (e.g. inside a modal).
    const handleNavigate = (!disableNavigation && systemId)
        ? () => navigate(`/systems/${systemId}/details`)
        : undefined;
    const clickableSx = handleNavigate ? { cursor: "pointer" } : undefined;

    const basicQuery = useQuery(SystemNameplateInfo, {
        variables: { id: systemId },
        skip: !systemId,
    });

    const iotQuery = useQuery(SystemIotConnection, {
        variables: { id: systemId },
        skip: !systemId,
    });

    if (basicQuery.loading) {
        return (
            <Box display="flex" flexDirection="column">
                <Box sx={{ paddingBottom: "12px" }} alignSelf="center">
                    <Skeleton variant="rectangular" width={200} height={200} sx={{ borderRadius: "8px" }} />
                </Box>
                <Skeleton variant="text" height={32} sx={{ alignSelf: "center", width: "80%" }} />
                <Box sx={{ padding: "12px 8px" }}>
                    <SkeletonKeyValue title={t('System Type')} />
                    <SkeletonKeyValue title={t('IMEI')} />
                </Box>
                <Skeleton variant="rectangular" height={32} sx={{ borderRadius: "16px" }} />
            </Box>
        );
    }

    if (basicQuery.error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={2}>
                <Typography level="body-sm" color="danger">
                    {t('Failed to load system information')}
                </Typography>
            </Box>
        );
    }

    // The installed base node title is the editable one (EditNodeModal); the backend
    // does not (yet) sync it back to system.title, so system.title is fallback only.
    const title = _.get(basicQuery.data, 'system.installedBaseNode.title') || _.get(basicQuery.data, 'system.title');
    const systemType = _.get(basicQuery.data, 'system.systemType');
    const systemTypeEntry = SYSTEM_TYPES.find(s => s.value === systemType);
    // Placeholder image is branded by the system's owning client (not the logged-in user).
    const clientIndustryType = _.get(basicQuery.data, 'system.client.industryType');
    // Systems have no image of their own — a user-set image on the system's node
    // wins, then the static PNG per system type, then the branded placeholder.
    const installedBaseNode = _.get(basicQuery.data, 'system.installedBaseNode');
    const image = getNodeImageThumbnail(installedBaseNode)
        ?? systemTypeEntry?.image
        ?? getFallbackAssetImage(clientIndustryType);
    const nodeImageTarget = buildNodeImageTarget(installedBaseNode);

    const iotData = _.get(iotQuery.data, 'system.iotConnection');
    const imei = _.get(iotData, 'gateway.imei');
    const connectionStatus = _.get(iotData, 'connectionStatus');

    return (
        <Box display="flex" flexDirection="column">
            {/* The action button is a sibling of the clickable box (whose click
                navigates), inside the hover-target wrapper it is positioned in. */}
            <Box className={NODE_IMAGE_HOVER_TARGET_CLASS} sx={{ position: "relative", marginBottom: "12px" }} alignSelf="center">
                <Box sx={clickableSx} onClick={handleNavigate}>
                    <img
                        src={image}
                        alt={systemTypeEntry ? t(systemTypeEntry.label) : t('System')}
                        style={{
                            width: 200,
                            height: 200,
                            objectFit: "contain",
                            borderRadius: 8,
                            display: "block",
                        }}
                    />
                </Box>
                {!hideImageActions && <NodeImageActionButton target={nodeImageTarget}/>}
            </Box>

            <Typography
                level="titleLargeBold"
                fontWeight="bold"
                textAlign="center"
                onClick={handleNavigate}
                sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    wordBreak: "break-word",
                    ...clickableSx,
                }}
            >
                {title}
            </Typography>

            <Box sx={{ padding: "12px 8px" }}>
                <KeyValueInformation title={t('System Type')} value={getSystemTypeLabel(systemType, t)} />
                {(iotQuery.loading || imei) && (
                    iotQuery.loading
                        ? <SkeletonKeyValue title={t('IMEI')} />
                        : <KeyValueInformation title={t('IMEI')} value={imei} />
                )}
            </Box>

            {(iotQuery.loading && !connectionStatus) ? (
                <Skeleton variant="rectangular" height={32} sx={{ borderRadius: "16px", width: "100%" }} />
            ) : (
                connectionStatus && (() => {
                    const { bg, text } = getStatusChipColors(connectionStatus);
                    return (
                        <Chip sx={{ maxWidth: "100%", textAlign: "center", width: "100%", backgroundColor: bg, color: text }}>
                            {getStatusChipLabel(t, connectionStatus, { isSystem: true })}
                        </Chip>
                    );
                })()
            )}

            {onOpenSystem && (
                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<OpenInNewOutlined />}
                    sx={{ mt: "13px" }}
                    onClick={onOpenSystem}
                >
                    {t("Open system")}
                </Button>
            )}
        </Box>
    );
};

export default SystemNameplate;
